import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      content: "(520) 200-7739 Ext 2",
      link: "tel:+15202007739,2",
    },
    {
      icon: Mail,
      title: "Email",
      content: "info@tucsonstumpgrinding.com",
      link: "mailto:info@tucsonstumpgrinding.com",
    },
    {
      icon: MapPin,
      title: "Service Area",
      content: "Tucson & Surrounding Areas, AZ",
      link: null,
    },
    {
      icon: Clock,
      title: "Hours",
      content: "Mon-Fri: 7am-6pm | Sat: 8am-2pm",
      link: null,
    },
  ];

  return (
    <section id="contact" className="py-16 lg:py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left - Contact Info */}
          <div>
            <div className="accent-line mb-4" />
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] uppercase mb-4">
              Get In Touch
            </h2>
            <p className="font-body text-lg text-gray-600 mb-8">
              Ready to get rid of those unsightly stumps? Contact us today for a free quote. We typically respond within a few hours during business days.
            </p>

            {/* Contact Cards */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {contactInfo.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#F5F5F0] border-2 border-[#E5E5E0] p-5 hover:border-[#1A1A1A] transition-colors"
                  data-testid={`contact-info-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF4D00] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-[#1A1A1A] uppercase text-sm mb-1">
                        {item.title}
                      </h3>
                      {item.link ? (
                        <a
                          href={item.link}
                          className="font-body text-sm text-gray-700 hover:text-[#FF4D00] transition-colors"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="font-body text-sm text-gray-700">{item.content}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Emergency Banner */}
            <div className="bg-[#1A1A1A] p-6 relative overflow-hidden noise-bg">
              <div className="relative z-10">
                <h3 className="font-heading text-lg font-bold text-white uppercase mb-2">
                  Need Urgent Service?
                </h3>
                <p className="font-body text-gray-400 text-sm mb-4">
                  For emergency stump grinding or tree hazard situations, call us directly.
                </p>
                <a
                  href="tel:+15202007739,2"
                  className="btn-primary px-6 py-3 inline-block text-sm"
                  data-testid="contact-emergency-btn"
                >
                  Call Now: (520) 200-7739 Ext 2
                </a>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-[#F5F5F0] border-2 border-[#E5E5E0] p-6 sm:p-8" data-testid="contact-form">
            <h3 className="font-heading text-2xl font-bold text-[#1A1A1A] uppercase mb-6">
              Send Us A Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-name" className="font-body font-semibold text-sm">
                    Full Name *
                  </Label>
                  <Input
                    id="contact-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 rounded-none bg-white border-2 border-[#E5E5E0] focus:border-[#FF4D00]"
                    placeholder="Your name"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-phone" className="font-body font-semibold text-sm">
                    Phone Number
                  </Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1 rounded-none bg-white border-2 border-[#E5E5E0] focus:border-[#FF4D00]"
                    placeholder="(520) 555-1234"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contact-email" className="font-body font-semibold text-sm">
                  Email Address *
                </Label>
                <Input
                  id="contact-email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 rounded-none bg-white border-2 border-[#E5E5E0] focus:border-[#FF4D00]"
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <Label htmlFor="contact-subject" className="font-body font-semibold text-sm">
                  Subject *
                </Label>
                <Input
                  id="contact-subject"
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="mt-1 rounded-none bg-white border-2 border-[#E5E5E0] focus:border-[#FF4D00]"
                  placeholder="What can we help with?"
                  data-testid="contact-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="contact-message" className="font-body font-semibold text-sm">
                  Message *
                </Label>
                <Textarea
                  id="contact-message"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="mt-1 rounded-none bg-white border-2 border-[#E5E5E0] focus:border-[#FF4D00]"
                  placeholder="Tell us about your project or question..."
                  rows={5}
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-4 rounded-none flex items-center justify-center gap-2"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    Send Message
                    <Send className="w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
